export const getCurrentInTimezone = (timeZone) => {
	return new Date();
};

export function getDateStringInTimeZone(date, timeZone) {
	return new Intl.DateTimeFormat('en-CA', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).format(date);
}

export function formatHHMMInTimezone(date, timeZone) {
	const parts = new Intl.DateTimeFormat('en-US', {
		hour12: false,
		hour: '2-digit',
		minute: '2-digit',
		timeZone,
	}).formatToParts(date);
	const hour = parts.find(p => p.type === 'hour').value;
	const minute = parts.find(p => p.type === 'minute').value;
	return `${hour}:${minute}`;
}

export const addMinutes = (date, minutes) => {
	const d = new Date(date);
	d.setMinutes(d.getMinutes() + minutes);
	return d;
};

export const isStartTimeInPastForToday = (startDate, startTime, timezone) => {
	const now = new Date();
	const today = getDateStringInTimeZone(now, timezone);
	if (startDate !== today) return false;
	const [startH, startM] = startTime.split(':').map(Number);
	const parts = new Intl.DateTimeFormat('en-US', {
		hour12: false,
		hour: '2-digit',
		minute: '2-digit',
		timeZone: timezone,
	}).formatToParts(now);
	const nowH = Number(parts.find(p => p.type === 'hour').value);
	const nowM = Number(parts.find(p => p.type === 'minute').value);
	const startTotalMinutes = startH * 60 + startM;
	const nowTotalMinutes = nowH * 60 + nowM;
	return startTotalMinutes < nowTotalMinutes;
};

export const applyOneTimeDefaults = (data) => {
	const tz = data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
	const now = getCurrentInTimezone(tz);
	const today = getDateStringInTimeZone(now, tz);
	let startDate = data.start_date || today;
	let startTime = data.start_time || formatHHMMInTimezone(now, tz);
	if (isStartTimeInPastForToday(startDate, startTime, tz)) {
		startTime = formatHHMMInTimezone(now, tz);
	}
	return {
		...data,
		timezone: tz,
		start_date: startDate,
		start_time: startTime,
		end_time: null,
		end_date: null,
	};
};

export const applyOngoingDefaults = (data) => {
	const tz = data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

	// Use updateTimesForTimezone to get initial start/end times and start date
	let updatedData = updateTimesForTimezone(data, tz);

	// Adjust startTime if it's in the past for today
	const { start_date: startDate, start_time: startTime } = updatedData;
	let endTime = updatedData.end_time;

	const now = getCurrentInTimezone(tz);
	if (isStartTimeInPastForToday(startDate, startTime, tz)) {
		updatedData.start_time = formatHHMMInTimezone(now, tz);
		if (endTime <= updatedData.start_time) {
			updatedData.end_time = formatHHMMInTimezone(addMinutes(now, 60), tz);
		}
	}
	return {
		...updatedData,
		end_date: data.end_date || null,
	};
};


// export const isTimeInPastToday = (dateStr, timeStr, timezone) => {
// 	const nowInTz = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
// 	const [hour, minute] = timeStr.split(':').map(Number);
// 	const inputDateTime = new Date(`${dateStr}T00:00:00`);
// 	inputDateTime.setHours(hour, minute, 0, 0);
// 	nowInTz.setSeconds(0, 0);
// 	return inputDateTime.toDateString() === nowInTz.toDateString() && inputDateTime < nowInTz;
// };

export const resetScheduledFields = (data) => ({
	...data,
	start_time: null,
	end_time: null,
	start_date: null,
	end_date: null,
	timezone: null,
});

export const updateTimesForTimezone = (data, timezoneValue) => {
	const now = new Date();
	const newStartTime = formatHHMMInTimezone(now, timezoneValue);
	const defaultEnd = addMinutes(now, 60);
	const formattedEndTime = formatHHMMInTimezone(defaultEnd, timezoneValue);
	const newStartDate = getDateStringInTimeZone(now, timezoneValue);
	let updatedData = {
		...data,
		timezone: timezoneValue,
		start_time: newStartTime,
	};
	updatedData.end_time = formattedEndTime;
	updatedData.start_date = newStartDate;
	return updatedData;
};

export const convertTo24HourFormat = (time12h) => {
	const [time, modifier] = time12h.split(" ");
	let [hours, minutes] = time.split(":");

	if (hours === "12") {
		hours = "00";
	}
	if (modifier === "PM") {
		hours = String(parseInt(hours, 10) + 12);
	}
	return `${hours.padStart(2, "0")}:${minutes}`;
}

export const formatDateForInput = (dateString) => {
	if (!dateString) return '';
	try {
		// Expecting format like "Sep 08, 2025"
		const [monthStr, day, year] = dateString.replace(',', '').split(' ');

		const monthMap = {
			Jan: '01', Feb: '02', Mar: '03', Apr: '04',
			May: '05', Jun: '06', Jul: '07', Aug: '08',
			Sep: '09', Oct: '10', Nov: '11', Dec: '12'
		};

		const month = monthMap[monthStr];
		return `${year}-${month}-${day.padStart(2, '0')}`; // YYYY-MM-DD
	} catch (error) {
		console.error('Error formatting date:', error);
		return '';
	}
};

export const getNowInTimezone = (timeZone) => {
	const now = new Date();
	const formatter = new Intl.DateTimeFormat("en-US", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	});

	const parts = formatter.formatToParts(now);
	const values = {};
	for (const p of parts) {
		if (p.type !== "literal") values[p.type] = p.value;
	}

	// Construct a Date in UTC corresponding to that timezone’s wall clock time
	return new Date(Date.UTC(
		Number(values.year),
		Number(values.month) - 1,
		Number(values.day),
		Number(values.hour),
		Number(values.minute),
		Number(values.second)
	));
};

export const isDateTimeInPast = (date, time, timeZone) => {
	const [year, month, day] = date.split("-").map(Number);
	const [hour, minute] = time.split(":").map(Number);

	const now = getNowInTimezone(timeZone); // <-- correct current time in timezone

	const inputDateUTC = Date.UTC(year, month - 1, day, hour, minute);
	const nowUTC = now.getTime();

	return inputDateUTC < nowUTC;
};

/**
 * Check if a given date is in the past (based on timezone).
 * @param {string} dateStr - Date string in format "YYYY-MM-DD"
 * @param {string} timezone - Timezone identifier (e.g., "UTC", "Asia/Kolkata")
 * @returns {boolean} - true if date is in the past, false otherwise
 */
export function isDateInPast(dateStr, timezone = "UTC") {
    if (!dateStr) return false;

    // Current date in given timezone
    const now = new Date();
    const tzNow = new Date(
        now.toLocaleString("en-US", { timeZone: timezone })
    );

    // Set to start of today (ignore time)
    tzNow.setHours(0, 0, 0, 0);

    // Input date (start of day)
    const inputDate = new Date(dateStr + "T00:00:00");

    return inputDate < tzNow;
}

/**
 * Normalize a phone number:
 * - Remove '+1' if present
 * - Remove all non-digit characters
 * - Trim spaces
 * @param {string} phone
 * @returns {string} normalized 10-digit phone number (digits only)
 */
export function normalizePhoneNumber(phone) {
    if (!phone) return '';

    // Trim spaces
    let normalized = phone.trim();

    // Remove +1 prefix if present
    if (normalized.startsWith('+1')) {
        normalized = normalized.slice(2);
		console.log(normalized);
    }

    // Remove all non-digit characters
    normalized = normalized.replace(/\D/g, '');

    // Optionally, keep only last 10 digits if someone entered country code mistakenly
    if (normalized.length > 10) {
        normalized = normalized.slice(-10);
    }

    return normalized;
}



